import React from 'react'
import { graphql, Slice } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import { format, parseISO } from 'date-fns'
import { PrismicRichText } from '@prismicio/react'
import { GatsbyImage } from 'gatsby-plugin-image'

// Components
import Breadcrumb from '../components/breadcrumb'
import Layout from '../components/layout'
import ContactPanel from '../components/contactPanel'
import Seo from '../components/seo'
import Share from '../components/share'

export const query = graphql`
  query Blogpost($id: String) {
    prismicBlogpost(id: { eq: $id }) {
      ...PrismicBlogpostFragment
    }
  }
`

const BlogpostTemplate = ({ data, location }) => {
  const page = data.prismicBlogpost
  const pageData = page.data
  let featured = false
  if (pageData.featured === true) {
    featured = true
  }

  let parseDate
  let formattedDate
  if (page.first_publication_date) {
    parseDate = parseISO(page.first_publication_date)
    formattedDate = format(new Date(parseDate), 'EEEE, do MMMM yyyy')
  }
  const wordsPerMinute = 200
  let wordCount = pageData.blogpost_text.text.split(' ').length
  let timeToRead = Math.ceil(wordCount / wordsPerMinute)

  const author = pageData.author[0]
  const authorData = author.author_link.document

  return (
    <Layout location={location}>
      <div className={`bg-gradient-to-tr from-fuchsia-800 via-indigo-800 to-teal-600 relative ${page.uid}`}>
        <div className={`h-full w-full max-w-screen-xl mx-auto ${pageData.blogpost_image && 'grid xl:grid-cols-2 xl:gap-4'}`}>
          <div className='sm:w-3/4 lg:w-full px-8 xl:px-0 z-10 flex flex-col items-center justify-center md:pb-24 md:pt-32 lg:pt-40 pb-12 pt-36 sm:items-start sm:pb-20 sm:text-left space-y-8 text-center text-white'>
            {featured && <div className='inline-block leading-6 tracking-wider py-0.5 px-1.5 rounded-sm uppercase bg-indigo-600 text-purple-50'>featured article</div>}
            <div className='drop-shadow'>
              <PrismicRichText field={pageData.blogpost_title.richText} />
            </div>
            <div className='text-center sm:text-left md:mt-10 sm:flex sm:items-center'>
              {authorData.data.page_image.gatsbyImageData && (
                <GatsbyImage
                  image={authorData.data.page_image.gatsbyImageData}
                  className='h-16 w-16 my-2 bg-white p-2 mx-auto sm:mx-0 rounded-lg rounded-bl-none border-2 border-white'
                  loading='eager'
                  alt={authorData.data.page_title.text || ''}
                  role='presentation'
                />
              )}
              <div className='sm:ml-4'>
                <a href={authorData.url} className='border-b hover:border-transparent font-semibold text-white'>
                  {authorData.data.page_title.text}
                </a>
                {parseDate && formattedDate && (
                  <div className='text-white'>
                    <time dateTime={parseDate}>{formattedDate}</time>
                  </div>
                )}
                <p className='text-gray-50 text-sm'>{timeToRead} min read</p>
              </div>
            </div>
          </div>
          {pageData.blogpost_image && (
            <div className='flex-1 z-0'>
              <div className='sm:absolute right-0 top-0 bottom-0 sm:w-1/2'>
                <GatsbyImage image={pageData.blogpost_image.gatsbyImageData} className='mask relative overflow-hidden sm:object-fill h-full w-full' alt={pageData.blogpost_image.alt || ''} />
              </div>
            </div>
          )}
        </div>
      </div>
      <Breadcrumb location={location} />
      <div className='mx-auto grid w-full max-w-screen-xl grid-cols-1 gap-6 lg:gap-10 pb-10 p-6 lg:px-0 lg:py-10 lg:grid-flow-dense lg:grid-cols-3'>
        <div className='prose prose-lg dark:prose-invert lg:col-span-2 min-w-full lg:col-start-1 flex flex-wrap blogpost'>
          <PrismicRichText field={pageData.blogpost_text.richText} />
        </div>
        {page.uid === 'how-to-create-a-stunning-backlight-effect-with-css' && (
          // Bit of a hack
          <div className='lg:col-span-2'>
            <div className='aspect-video bg-black p-10 rounded-xl'>
              <iframe
                class='filter-blur w-full aspect-video'
                width='1280'
                height='auto'
                src='https://www.youtube.com/embed/A3ZZj5y6Qt4'
                title='IDLES - DANCER  (Official Video)'
                frameborder='0'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                referrerpolicy='strict-origin-when-cross-origin'
                allowfullscreen></iframe>
            </div>
          </div>
        )}
        <div className='lg:col-span-1 lg:col-start-3 border-t pt-6 lg:pt-0 lg:border-t-0 lg:border-l lg:px-8'>
          <div className='top-16 flex flex-col gap-12 lg:sticky'>
            <Share title={pageData.blogpost_title.text} link={page.url} image={pageData?.page_image} />
            <div>
              <div className='prose mb-3 dark:prose-invert lg:mb-6'>
                <h2>Other articles you may enjoy</h2>
              </div>
              <Slice alias='posts' id={data.prismicBlogpost?.id} layout='list' type={page.type} />
            </div>
          </div>
        </div>
      </div>
      <ContactPanel />
    </Layout>
  )
}

export default withPrismicPreview(BlogpostTemplate)

export const Head = ({ data, location }) => {
  const pageData = data.prismicBlogpost.data
  return (
    <Seo
      authorData={pageData.author[0]}
      datePublished={data.prismicBlogpost.first_publication_date}
      dateModified={data.prismicBlogpost.last_publication_date}
      description={pageData.meta_description}
      image={pageData.blogpost_image.url}
      isBlogPost={true}
      pathname={location.pathname}
      title={pageData.meta_title ? pageData.meta_title : pageData.blogpost_title.text}
    />
  )
}
